import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  DASHBOARD_ALPHA_KEY,
  DashboardUserLayoutPayload,
  DashboardWidgetConfig,
  DEFAULT_DASHBOARD_LAYOUT,
  mergeWithDefaultLayout,
} from '../models/dashboard-layout.model';
import { UserDetailService } from './user-detail.service';

interface LayoutApiResponse {
  status?: number;
  message?: string;
  isAuthorized?: boolean;
  data?: DashboardUserLayoutPayload;
  widgets?: DashboardWidgetConfig[];
}

@Injectable({
  providedIn: 'root',
})
export class DashboardLayoutService {
  /** Implemented on PSS capi; gapi may return 403 until route is registered there. */
  private readonly layoutUrlPss =
    environment.pssServerUrl + 'dashboard/userLayout';
  private readonly layoutUrlGapi =
    environment.clientUrl + 'dashboard/userLayout';

  private readonly editModeSubject = new BehaviorSubject<boolean>(false);
  private readonly layoutSubject = new BehaviorSubject<DashboardWidgetConfig[]>(
    DEFAULT_DASHBOARD_LAYOUT.map((w) => ({ ...w }))
  );
  private draftLayout: DashboardWidgetConfig[] | null = null;
  private layoutLoaded = false;

  readonly editMode$ = this.editModeSubject.asObservable();
  readonly layout$ = this.layoutSubject.asObservable();

  constructor(
    private http: HttpClient,
    private userDetailService: UserDetailService
  ) {}

  get editMode(): boolean {
    return this.editModeSubject.value;
  }

  get layout(): DashboardWidgetConfig[] {
    return this.layoutSubject.value;
  }

  get sortedLayout(): DashboardWidgetConfig[] {
    return [...this.layoutSubject.value].sort((a, b) => a.order - b.order);
  }

  loadUserLayout(dashboardKey: string = DASHBOARD_ALPHA_KEY): Promise<DashboardWidgetConfig[]> {
    return this.requestLayout('get', dashboardKey).catch(() => {
      const merged = mergeWithDefaultLayout(null);
      this.applyLayout(merged);
      this.layoutLoaded = true;
      return merged;
    });
  }

  enterEditMode(): void {
    if (!this.layoutLoaded) {
      this.draftLayout = mergeWithDefaultLayout(null);
    } else {
      this.draftLayout = this.layoutSubject.value.map((w) => ({ ...w }));
    }
    this.editModeSubject.next(true);
  }

  cancelEditMode(): void {
    if (this.draftLayout) {
      this.applyLayout(this.draftLayout.map((w) => ({ ...w })));
    }
    this.draftLayout = null;
    this.editModeSubject.next(false);
  }

  exitEditModeAfterSave(): void {
    this.draftLayout = null;
    this.editModeSubject.next(false);
  }

  updateDraft(widgets: DashboardWidgetConfig[]): void {
    this.applyLayout(widgets);
  }

  reorderWidgets(previousIndex: number, currentIndex: number): void {
    const list = this.sortedLayout.map((w) => ({ ...w }));
    const [moved] = list.splice(previousIndex, 1);
    list.splice(currentIndex, 0, moved);
    list.forEach((w, i) => (w.order = i));
    this.applyLayout(list);
  }

  setWidgetColSpan(id: string, colSpan: 4 | 6 | 8 | 12): void {
    this.applyLayout(
      this.layoutSubject.value.map((w) =>
        w.id === id ? { ...w, colSpan } : { ...w }
      )
    );
  }

  setWidgetVisible(id: string, visible: boolean): void {
    this.applyLayout(
      this.layoutSubject.value.map((w) =>
        w.id === id ? { ...w, visible } : { ...w }
      )
    );
  }

  toggleWidgetCollapsed(id: string): void {
    this.applyLayout(
      this.layoutSubject.value.map((w) =>
        w.id === id ? { ...w, collapsed: !w.collapsed } : { ...w }
      )
    );
  }

  persistLayout(dashboardKey: string = DASHBOARD_ALPHA_KEY): Promise<DashboardWidgetConfig[]> {
    return this.putLayout(dashboardKey, false);
  }

  saveUserLayout(dashboardKey: string = DASHBOARD_ALPHA_KEY): Promise<DashboardWidgetConfig[]> {
    return this.putLayout(dashboardKey, true);
  }

  resetToDefaultLayout(): void {
    this.applyLayout(DEFAULT_DASHBOARD_LAYOUT.map((w) => ({ ...w })));
  }

  private putLayout(
    dashboardKey: string,
    exitEdit: boolean
  ): Promise<DashboardWidgetConfig[]> {
    const payload: DashboardUserLayoutPayload = {
      dashboardKey,
      widgets: this.sortedLayout,
    };
    return this.requestLayout('put', dashboardKey, payload).then((merged) => {
      if (exitEdit) {
        this.exitEditModeAfterSave();
      }
      return merged;
    });
  }

  private requestLayout(
    method: 'get' | 'put',
    dashboardKey: string,
    payload?: DashboardUserLayoutPayload
  ): Promise<DashboardWidgetConfig[]> {
    const headers = this.buildHeaders();
    const urls = [this.layoutUrlPss, this.layoutUrlGapi];
    let lastError: Error = new Error('Unable to save dashboard layout.');

    const attempt = (index: number): Promise<DashboardWidgetConfig[]> => {
      if (index >= urls.length) {
        return Promise.reject(lastError);
      }
      const url = urls[index];
      const req =
        method === 'get'
          ? this.http.get<LayoutApiResponse>(url, {
              headers,
              params: { dashboardKey },
            })
          : this.http.put<LayoutApiResponse>(url, payload, { headers });

      return req.toPromise().then((response) => {
        if (!this.isSuccessResponse(response)) {
          lastError = new Error(
            response?.message ||
              'Dashboard layout could not be saved. Please contact your administrator.'
          );
          return attempt(index + 1);
        }
        const merged = this.extractWidgets(response, payload?.widgets);
        this.applyLayout(merged);
        this.layoutLoaded = true;
        return merged;
      }).catch((err) => {
        lastError =
          err instanceof Error
            ? err
            : new Error('Dashboard layout request failed.');
        return attempt(index + 1);
      });
    };

    return attempt(0);
  }

  private isSuccessResponse(response: LayoutApiResponse | undefined): boolean {
    if (!response || typeof response !== 'object') {
      return false;
    }
    if (response.isAuthorized === false) {
      return false;
    }
    if (typeof response.status === 'number' && response.status !== 0) {
      return false;
    }
    return !!(response.data?.widgets || response.widgets);
  }

  private extractWidgets(
    response: LayoutApiResponse,
    fallback?: DashboardWidgetConfig[]
  ): DashboardWidgetConfig[] {
    const widgets =
      response?.data?.widgets ?? response?.widgets ?? fallback ?? null;
    return mergeWithDefaultLayout(Array.isArray(widgets) ? widgets : null);
  }

  private buildHeaders(): HttpHeaders {
    const companyDetails = localStorage.getItem('companyDetailsGoveva');
    const headersConfig: Record<string, string> = {
      'Content-Type': 'application/json',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      PSSDetail: this.userDetailService.pssDetail() || '',
      UserDetail: this.userDetailService.getUserDetail(),
      'x-access-token': localStorage.getItem('token') || '',
    };
    if (companyDetails && companyDetails !== 'null') {
      headersConfig.GovevaCompany = companyDetails;
    }
    return new HttpHeaders(headersConfig);
  }

  private applyLayout(widgets: DashboardWidgetConfig[]): void {
    const normalized = mergeWithDefaultLayout(widgets);
    this.layoutSubject.next(normalized);
  }
}
